import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { apiError, isNotFound, useStatementQuery, useStatementsQuery } from '@/store/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Select } from '@/components/ui/select'
import { ErrorState } from '@/components/ui/empty-state'
import { StatementView } from '@/components/panels/StatementView'
import { recentPeriods } from '@/components/ui/period-select'
import { NotFound } from '@/pages/NotFound'
import { fmtMoney, fmtPeriod } from '@/lib/format'

/**
 * One statement per Billing Period. Closed periods read back exactly as frozen; the
 * current period is a provisional preview that changes until Atarim closes it.
 */
export function Statements() {
  const { slug = '' } = useParams()
  const { data: closed, error: listError } = useStatementsQuery(slug)
  const current = recentPeriods(1)[0]
  const [period, setPeriod] = useState<string | null>(null)
  const selected = period ?? closed?.[0]?.period ?? current
  const { data, isLoading, isError, error, refetch } = useStatementQuery({ slug, period: selected })

  if (isNotFound(listError)) return <NotFound />

  const closedSet = new Set(closed?.map((c) => c.period))
  const options = [
    ...(closedSet.has(current) ? [] : [{ value: current, label: `${fmtPeriod(current)} — provisional` }]),
    ...(closed ?? []).map((c) => ({ value: c.period, label: `${fmtPeriod(c.period)} — ${fmtMoney(c.totalUsdMinor, 'USD')}` })),
  ]

  return (
    <>
      <PageHeader
        title="Statements"
        description="What Atarim invoices you for each Billing Period: Licence Fees for the period ahead, Expansion Charges for the period just gone, and any adjustments. Payment is due 14 days after the statement date."
        actions={<Select ariaLabel="Statement" value={selected} onValueChange={setPeriod} options={options} />}
      />
      {isError ? (
        <ErrorState message={apiError(error)} onRetry={refetch} />
      ) : isLoading || !data ? (
        <div className="h-72 animate-pulse rounded-xl bg-muted" />
      ) : (
        <StatementView statement={data} downloadBase={`/api/partners/${slug}/statements/${selected}`} />
      )}
    </>
  )
}
